import React, { useState, useEffect } from 'react';

export default function BankManagement({
  selectedTile,
  userId,
  depositToBank,
  withdrawFromBank,
  getBankDeposit,
  getTotalBankDeposits,
  getCurrentPlayer,
  setBankInterestRate,
  getBankInterestRate,
  onSellProperty,
  getTileUpgradeLevel,
  getUpgradeCost,
  getPlayerLoans,
  checkCanSellBank,
  getBankLoans,
  gameData // Add this prop to access connection
}) {
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [showSellConfirmation, setShowSellConfirmation] = useState(false);
  const [canSellBank, setCanSellBank] = useState(true);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  if (!selectedTile || selectedTile.type !== 13 || !selectedTile.owner || selectedTile.owner.owner !== userId) {
    return null;
  }

  // Check cooldown status when component mounts and update every second
  useEffect(() => {
    if (!gameData?.connection || !selectedTile?.key) return;

    const checkCooldown = () => {
      gameData.connection.invoke('GetInterestRateCooldown', gameData.gameRoomId, selectedTile.key);
    };

    // Initial check
    checkCooldown();

    // Set up interval to check every second
    const intervalId = setInterval(checkCooldown, 1000);

    // Listen for cooldown updates
    const handleCooldownInfo = (data) => {
      if (data.bankTileKey === selectedTile.key) {
        setCooldownSeconds(Math.ceil(data.cooldownSeconds));
      }
    };

    gameData.connection.on('InterestRateCooldownInfo', handleCooldownInfo);

    return () => {
      clearInterval(intervalId);
      gameData.connection.off('InterestRateCooldownInfo', handleCooldownInfo);
    };
  }, [gameData?.connection, gameData?.gameRoomId, selectedTile?.key]);

  const handleDeposit = () => {
    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid deposit amount');
      return;
    }
    
    const currentPlayer = getCurrentPlayer();
    if (!currentPlayer || currentPlayer.budget < amount) {
      alert('Insufficient budget for this deposit');
      return;
    }
    
    depositToBank(selectedTile.key, amount);
    setDepositAmount('');
  };

  const handleWithdraw = () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid withdrawal amount');
      return;
    }
    
    const currentDeposit = getBankDeposit(selectedTile.key);
    if (currentDeposit < amount) {
      alert('Insufficient deposit balance for this withdrawal');
      return;
    }
    
    withdrawFromBank(selectedTile.key, amount);
    setWithdrawAmount('');
  };

  const handleSetInterestRate = () => {
    const rate = parseFloat(interestRate);
    if (isNaN(rate) || rate < 0 || rate > 50) {
      alert('Please enter a valid interest rate between 0% and 50%');
      return;
    }
    
    if (cooldownSeconds > 0) {
      alert(`Interest rate can only be changed once every 30 seconds. Please wait ${cooldownSeconds} second${cooldownSeconds !== 1 ? 's' : ''} before changing again.`);
      return;
    }
    
    setBankInterestRate(selectedTile.key, rate);
    setInterestRate('');
  };

  // Check if bank can be sold when component mounts or relevant data changes
  useEffect(() => {
    if (selectedTile && checkCanSellBank) {
      const canSell = checkCanSellBank(selectedTile.key);
      setCanSellBank(canSell);
    }
  }, [selectedTile, checkCanSellBank, getTotalBankDeposits, getPlayerLoans]);

  const calculateSellPrice = () => {
    const purchasePrice = selectedTile.owner.purchasePrice || 0;
    const upgradeLevel = getTileUpgradeLevel ? getTileUpgradeLevel(selectedTile.key) : 0;
    
    let totalUpgradeCost = 0;
    for (let level = 1; level <= upgradeLevel; level++) {
      totalUpgradeCost += getUpgradeCost ? getUpgradeCost(level) : 0;
    }
    
    return (purchasePrice * 0.5) + (totalUpgradeCost * 0.5);
  };

  const handleSellClick = () => {
    setShowSellConfirmation(true);
  };

  const handleConfirmSell = () => {
    onSellProperty(selectedTile.key);
    setShowSellConfirmation(false);
  };

  const handleCancelSell = () => {
    setShowSellConfirmation(false);
  };

  const sellPrice = calculateSellPrice();
  const upgradeLevel = getTileUpgradeLevel ? getTileUpgradeLevel(selectedTile.key) : 0;
  const totalDeposits = getTotalBankDeposits();
  const activeLoans = getPlayerLoans ? getPlayerLoans().filter(loan => loan.bankTileKey === selectedTile.key && loan.isActive) : [];
  const bankLoans = getBankLoans ? getBankLoans(selectedTile.key) : [];

  const isRateChangeDisabled = !interestRate || parseFloat(interestRate) < 0 || parseFloat(interestRate) > 50 || cooldownSeconds > 0;

  return (
    <div style={{
      marginBottom: '15px',
      padding: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '5px',
      border: '1px solid rgba(255, 255, 255, 0.3)'
    }}>
      <h5 style={{ margin: '0 0 10px 0', fontSize: '0.85em', color: '#E8F5E8', wordWrap: 'break-word' }}>
        💰 Bank Management
      </h5>
      
      <div style={{ marginBottom: '10px', fontSize: '0.75em', color: '#C8E6C9', wordWrap: 'break-word' }}>
        Total Bank Deposits: ${getTotalBankDeposits()?.toLocaleString() || '0'}
      </div>
      
      <div style={{ marginBottom: '10px', fontSize: '0.75em', color: '#C8E6C9', wordWrap: 'break-word' }}>
        Available Budget: ${getCurrentPlayer()?.budget.toLocaleString() || '0'}
      </div>
      
      {/* Interest Rate Section */}
      <div style={{ marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
        <div style={{ fontSize: '0.8em', marginBottom: '5px', color: '#E8F5E8' }}>Interest Rate Management:</div>
        <div style={{ fontSize: '0.75em', marginBottom: '5px', color: '#C8E6C9' }}>
          Current Rate: {getBankInterestRate(selectedTile.key)}% per minute
        </div>
        {cooldownSeconds > 0 && (
          <div style={{ 
            fontSize: '0.7em', 
            marginBottom: '5px', 
            color: '#FFB74D',
            backgroundColor: 'rgba(255, 183, 77, 0.1)',
            padding: '4px 8px',
            borderRadius: '3px',
            textAlign: 'center',
            border: '1px solid rgba(255, 183, 77, 0.3)'
          }}>
            ⏱️ Rate change available in {cooldownSeconds}s
            <div style={{ 
              fontSize: '0.9em', 
              marginTop: '2px', 
              opacity: 0.8,
              fontStyle: 'italic'
            }}>
              (30 second cooldown between changes)
            </div>
          </div>
        )}
        <input
          type="number"
          value={interestRate}
          onChange={(e) => setInterestRate(e.target.value)}
          placeholder="New interest rate (0-50%)"
          min="0"
          max="50"
          step="0.1"
          disabled={cooldownSeconds > 0}
          style={{
            width: '100%',
            padding: '5px',
            marginBottom: '5px',
            borderRadius: '3px',
            border: 'none',
            fontSize: '0.75em',
            boxSizing: 'border-box',
            opacity: cooldownSeconds > 0 ? 0.6 : 1,
            cursor: cooldownSeconds > 0 ? 'not-allowed' : 'text'
          }}
        />
        <button
          onClick={handleSetInterestRate}
          disabled={isRateChangeDisabled}
          style={{
            width: '100%',
            padding: '5px',
            backgroundColor: isRateChangeDisabled ? '#666' : '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: isRateChangeDisabled ? 'not-allowed' : 'pointer',
            fontSize: '0.75em',
            opacity: isRateChangeDisabled ? 0.6 : 1
          }}
          title={cooldownSeconds > 0 ? `Wait ${cooldownSeconds} seconds before changing rate again` : 'Set Interest Rate'}
        >
          {cooldownSeconds > 0 ? `⏱️ Wait ${cooldownSeconds}s` : 'Set Interest Rate'}
        </button>
      </div>
      
      {/* Deposit Section */}
      <div style={{ marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
        <div style={{ fontSize: '0.8em', marginBottom: '5px', color: '#E8F5E8' }}>Deposit Money:</div>
        <input
          type="number"
          value={depositAmount}
          onChange={(e) => setDepositAmount(e.target.value)}
          placeholder="Amount to deposit"
          style={{
            width: '100%',
            padding: '5px',
            marginBottom: '5px',
            borderRadius: '3px',
            border: 'none',
            fontSize: '0.75em',
            boxSizing: 'border-box'
          }}
        />
        <button
          onClick={handleDeposit}
          disabled={!depositAmount || parseFloat(depositAmount) <= 0 || (getCurrentPlayer()?.budget || 0) < parseFloat(depositAmount || '0')}
          style={{
            width: '100%',
            padding: '5px',
            backgroundColor: (!depositAmount || parseFloat(depositAmount) <= 0 || (getCurrentPlayer()?.budget || 0) < parseFloat(depositAmount || '0')) ? '#666' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: (!depositAmount || parseFloat(depositAmount) <= 0 || (getCurrentPlayer()?.budget || 0) < parseFloat(depositAmount || '0')) ? 'not-allowed' : 'pointer',
            fontSize: '0.75em',
            opacity: (!depositAmount || parseFloat(depositAmount) <= 0 || (getCurrentPlayer()?.budget || 0) < parseFloat(depositAmount || '0')) ? 0.6 : 1
          }}
        >
          Deposit
        </button>
      </div>
      
      {/* Withdraw Section */}
      <div style={{ marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
        <div style={{ fontSize: '0.8em', marginBottom: '5px', color: '#E8F5E8' }}>Withdraw Money:</div>
        <input
          type="number"
          value={withdrawAmount}
          onChange={(e) => setWithdrawAmount(e.target.value)}
          placeholder="Amount to withdraw"
          style={{
            width: '100%',
            padding: '5px',
            marginBottom: '5px',
            borderRadius: '3px',
            border: 'none',
            fontSize: '0.75em',
            boxSizing: 'border-box'
          }}
        />
        <button
          onClick={handleWithdraw}
          disabled={!withdrawAmount || parseFloat(withdrawAmount) <= 0 || (getBankDeposit(selectedTile.key) || 0) < parseFloat(withdrawAmount || '0')}
          style={{
            width: '100%',
            padding: '5px',
            backgroundColor: (!withdrawAmount || parseFloat(withdrawAmount) <= 0 || (getBankDeposit(selectedTile.key) || 0) < parseFloat(withdrawAmount || '0')) ? '#666' : '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: (!withdrawAmount || parseFloat(withdrawAmount) <= 0 || (getBankDeposit(selectedTile.key) || 0) < parseFloat(withdrawAmount || '0')) ? 'not-allowed' : 'pointer',
            fontSize: '0.75em',
            opacity: (!withdrawAmount || parseFloat(withdrawAmount) <= 0 || (getBankDeposit(selectedTile.key) || 0) < parseFloat(withdrawAmount || '0')) ? 0.6 : 1
          }}
        >
          Withdraw
        </button>
      </div>
    </div>
  );
}